import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey, useTransport } from '@connectrpc/connect-query'
import type { QueryClient, QueryKey } from '@tanstack/react-query'

import {
  getEmailThread,
  listEmails,
  listEmailThreadEntityCandidates,
  listEmailThreadLinks,
  listEmailThreads,
  listEmailThreadsByEntity,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import { ListEmailThreadLinksRequestSchema } from '@/gen/cortado/v1/email_conversations_pb'
import {
  GetEmailThreadRequestSchema,
  ListEmailThreadsResponseSchema,
  type EmailThread,
  type EmailThreadLite,
  type EmailThreadStatus,
  type ListEmailThreadsResponse,
} from '@/gen/cortado/v1/emails_pb'

export function getOrganizationIdFromQueryKey(queryKey: unknown): string | undefined {
  if (!Array.isArray(queryKey)) return undefined

  const queryOptions = queryKey[1]
  if (!queryOptions || typeof queryOptions !== 'object') return undefined

  const input = (queryOptions as { input?: unknown }).input
  if (!input || typeof input !== 'object') return undefined

  const organizationId = (input as { organizationId?: unknown }).organizationId
  return typeof organizationId === 'string' ? organizationId : undefined
}

interface ListEmailThreadsCacheInput {
  organizationId?: string
  statuses?: EmailThreadStatus[]
  assignedTo?: string
  inquiryId?: string
  searchQuery?: string
  view?: number
  limit?: number
}

function getListEmailThreadsInputFromQueryKey(queryKey: unknown): ListEmailThreadsCacheInput | undefined {
  if (!Array.isArray(queryKey)) return undefined

  const queryOptions = queryKey[1]
  if (!queryOptions || typeof queryOptions !== 'object') return undefined

  const input = (queryOptions as { input?: unknown }).input
  if (!input || typeof input !== 'object') return undefined

  const raw = input as Record<string, unknown>
  return {
    organizationId: typeof raw.organizationId === 'string' ? raw.organizationId : undefined,
    statuses: Array.isArray(raw.statuses)
      ? raw.statuses.filter((status): status is EmailThreadStatus => typeof status === 'number')
      : undefined,
    assignedTo: typeof raw.assignedTo === 'string' ? raw.assignedTo : undefined,
    inquiryId: typeof raw.inquiryId === 'string' ? raw.inquiryId : undefined,
    searchQuery: typeof raw.searchQuery === 'string' ? raw.searchQuery : undefined,
    view: typeof raw.view === 'number' ? raw.view : undefined,
    limit: typeof raw.limit === 'number' ? raw.limit : undefined,
  }
}

function emailThreadMatchesListInput(
  thread: Pick<EmailThreadLite, 'organizationId' | 'status' | 'assignedTo' | 'inquiryId'>,
  input: ListEmailThreadsCacheInput | undefined,
) {
  if (!input) return true
  if (input.organizationId && thread.organizationId !== input.organizationId) return false
  if (input.statuses && input.statuses.length > 0 && !input.statuses.includes(thread.status)) return false
  if (input.assignedTo && thread.assignedTo !== input.assignedTo) return false
  if (input.inquiryId && thread.inquiryId !== input.inquiryId) return false
  return true
}

function applyEmailThreadUpdateToLite(thread: EmailThreadLite, updatedThread: EmailThread): EmailThreadLite {
  return {
    ...thread,
    organizationId: updatedThread.organizationId,
    subject: updatedThread.subject,
    status: updatedThread.status,
    participants: updatedThread.participants,
    assignedTo: updatedThread.assignedTo,
    messageCount: updatedThread.messageCount,
    lastMessageAt: updatedThread.lastMessageAt,
    lastMessageDirection: updatedThread.lastMessageDirection,
    contactId: updatedThread.contactId,
    inquiryId: updatedThread.inquiryId,
    createdAt: updatedThread.createdAt ?? thread.createdAt,
    snoozedUntil: updatedThread.snoozedUntil,
  }
}

export function findCachedEmailThreadLite(
  listEntries: Array<[QueryKey, ListEmailThreadsResponse | undefined]>,
  threadId: string,
) {
  for (const [, data] of listEntries) {
    const thread = data?.threads.find((candidate) => candidate.id === threadId)
    if (thread) return thread
  }
  return undefined
}

export function updateCachedEmailThreadLists(
  queryClient: QueryClient,
  listEntries: Array<[QueryKey, ListEmailThreadsResponse | undefined]>,
  previousThread: EmailThreadLite | undefined,
  updatedThread: EmailThread,
) {
  const comparableUpdatedThread = previousThread
    ? applyEmailThreadUpdateToLite(previousThread, updatedThread)
    : updatedThread

  for (const [queryKey, data] of listEntries) {
    if (!data) continue

    const input = getListEmailThreadsInputFromQueryKey(queryKey)
    // Inbox tabs depend on per-user mute and on lead assignment, which this
    // cache cannot see; the list invalidation that follows refreshes them.
    if (input?.view) continue
    const existingIndex = data.threads.findIndex((thread) => thread.id === updatedThread.id)
    const previousMatches = previousThread ? emailThreadMatchesListInput(previousThread, input) : existingIndex >= 0
    const nextMatches = emailThreadMatchesListInput(comparableUpdatedThread, input)
    const isCountOnly = input?.limit === 0

    let totalDelta = 0
    if (previousMatches && !nextMatches) {
      totalDelta = -1
    } else if (!previousMatches && nextMatches) {
      totalDelta = 1
    }

    let threads = data.threads
    if (!isCountOnly && existingIndex >= 0) {
      threads = nextMatches
        ? data.threads.map((thread) =>
            thread.id === updatedThread.id ? applyEmailThreadUpdateToLite(thread, updatedThread) : thread,
          )
        : data.threads.filter((thread) => thread.id !== updatedThread.id)
    }

    queryClient.setQueryData(
      queryKey,
      create(ListEmailThreadsResponseSchema, {
        threads,
        totalCount: Math.max(0, data.totalCount + totalDelta),
        unreadCount: data.unreadCount,
        viewCounts: data.viewCounts,
      }),
    )
  }
}

export function invalidateEmailThreadLinkCaches(
  queryClient: QueryClient,
  transport: ReturnType<typeof useTransport>,
  variables: { organizationId?: string; threadId?: string },
) {
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmailThreadLinks,
      transport,
      input: create(ListEmailThreadLinksRequestSchema, {
        organizationId: variables.organizationId ?? '',
        threadId: variables.threadId ?? '',
      }),
      cardinality: 'finite',
    }),
  })
  // Any entity's Conversations panel could currently be showing this thread.
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmailThreadsByEntity,
      transport,
      cardinality: undefined,
    }),
  })
  // Linking a lead or booking can answer the thread's "Which event is this
  // about?" question, which changes its status and candidate list.
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmailThreadEntityCandidates,
      transport,
      cardinality: undefined,
    }),
  })
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmailThreads,
      transport,
      cardinality: undefined,
    }),
  })
  if (variables.threadId) {
    queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: getEmailThread,
        transport,
        input: create(GetEmailThreadRequestSchema, { id: variables.threadId }),
        cardinality: 'finite',
      }),
    })
  }
}

/**
 * Invalidates the caches that any mutation changing a thread's messages
 * needs to refresh beyond its own thread-detail/list queries: any linked
 * entity's Conversations panel (listEmailThreadsByEntity), the cross-entity
 * email history used by the Activity tab (listEmails), and the activity feed
 * itself. Mirrors what the compose send path already does; extracted so
 * reply and other message-mutating hooks can't drift from it independently.
 */
export function invalidateCrossEntityEmailCaches(queryClient: QueryClient, transport: ReturnType<typeof useTransport>) {
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmailThreadsByEntity,
      transport,
      cardinality: undefined,
    }),
  })
  queryClient.invalidateQueries({
    queryKey: createConnectQueryKey({
      schema: listEmails,
      transport,
      cardinality: undefined,
    }),
  })
  queryClient.invalidateQueries({ queryKey: ['activities'] })
}
